import React, { FC, useEffect, useMemo, useState } from "react";
import {
  Table,
  Descriptions,
  Tag,
  Empty,
  Collapse,
  Popover,
} from "antd";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import Swal from "sweetalert2";

import ProjectPageShell, {
  useProjectContext,
} from "../shared/ProjectPageShell";
import { GenericStatusPill } from "../shared/statusColors";
import purchaseOrderApis from "../../../api/master/PurchaseOrder";

const fmtDate = (d?: string) => (d ? dayjs(d).format("DD-MMM-YYYY") : "-");

const fmtAddress = (val: any): string => {
  if (val == null) return "-";
  if (typeof val === "string") return val;
  if (typeof val === "object") {
    const parts = [
      val.deliveryLocationName,
      val.addressLine1,
      val.addressLine2,
      val.cityName,
      val.districtName,
      val.stateName,
      val.countryName,
      val.pincode,
    ].filter((p) => p && String(p).trim().length > 0);
    return parts.length ? parts.join(", ") : "-";
  }
  return String(val);
};

const AddressPopover = ({ value }: { value: any }) => {
  if (!value || typeof value !== "object") {
    return <span>{value || "-"}</span>;
  }
  const short = value?.deliveryLocationName || value?.addressLine1 || "-";
  return (
    <Popover
      title="Address"
      content={
        <div style={{ maxWidth: 320, fontSize: 12 }}>
          {value.addressLine1 ? <div>{value.addressLine1}</div> : null}
          {value.addressLine2 ? <div>{value.addressLine2}</div> : null}
          <div>
            {[value.cityName, value.districtName, value.stateName, value.countryName]
              .filter(Boolean)
              .join(", ")}
          </div>
          {value.pincode ? <div>PIN: {value.pincode}</div> : null}
          {value.contactPersonName ? (
            <div style={{ marginTop: 6 }}>
              <strong>Contact:</strong> {value.contactPersonName}
              {value.mobileNumber ? ` (${value.mobileNumber})` : ""}
            </div>
          ) : null}
        </div>
      }
    >
      <span style={{ color: "#2557d6", cursor: "pointer", textDecoration: "underline" }}>
        {short}
      </span>
    </Popover>
  );
};

const num = (v: any) => {
  const n = Number(v);
  return isFinite(n) ? n : 0;
};

const ViewPurchaseOrder: FC = () => {
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const navigate = useNavigate();
  const { projectName } = useProjectContext();

  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<any>({});
  const [lines, setLines] = useState<any[]>([]);
  const [attachments, setAttachments] = useState<any[]>([]);

  useEffect(() => {
    if (orderNumber) fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNumber]);

  const fetchOrder = async () => {
    try {
      setLoading(true);
      const res: any = await purchaseOrderApis.fetchOrder(orderNumber!);
      const { data, response, status, message } = res || {};
      if (status) {
        const payload =
          (data?.lineItems || data?.supplierName ? data : null) ||
          (response?.lineItems || response?.supplierName ? response : null) ||
          data?.data ||
          response?.data ||
          data ||
          response ||
          {};
        setOrder(payload);
        setLines(payload?.lineItems || []);
        setAttachments(payload?.dms || []);
      } else if (message) {
        Swal.fire("Error", message, "error");
      }
    } catch (err) {
      console.error("Failed to fetch PO:", err);
    } finally {
      setLoading(false);
    }
  };

  const cur = order?.currencySymbol || "";

  const isOrderLevelDate =
    (order?.needByDateType || "").toLowerCase().includes("order");
  const isLineLevelAddress =
    (order?.deliveryAddressType || "").toLowerCase().includes("line");
  const isIgst = num(order?.igst) > 0;
  const isTransferred = num(order?.isOrderTransferred) === 1;

  const totals = useMemo(() => {
    let taxable = 0;
    let cgst = 0;
    let sgst = 0;
    let igst = 0;
    let gross = 0;
    lines.forEach((row: any) => {
      const qty = num(row?.quantity);
      const unit = num(row?.unitPrice ?? row?.price);
      const lineTaxable = qty * unit;
      taxable += lineTaxable;
      cgst += num(row?.cgst);
      sgst += num(row?.sgst);
      igst += num(row?.igst);
      const lineGross =
        num(row?.grossTotal) ||
        num(row?.totalAmount) ||
        num(row?.totalValue) ||
        lineTaxable + num(row?.cgst) + num(row?.sgst) + num(row?.igst);
      gross += lineGross;
    });
    return { taxable, cgst, sgst, igst, gross };
  }, [lines]);

  const headerStatus = order?.orderStatus || order?.status;

  const lineColumns: any[] = [
    {
      title: "#",
      key: "lineNo",
      width: 50,
      render: (_: any, _r: any, idx: number) => idx + 1,
    },
    {
      title: "Material Name",
      dataIndex: "materialName",
      key: "materialName",
      width: 200,
      fixed: "left" as const,
    },
    {
      title: "Make",
      dataIndex: "makeInfo",
      key: "makeInfo",
      width: 140,
      render: (v: any, row: any) => {
        if (Array.isArray(v) && v.length > 0) {
          return v.map((m: any) => m.makeName).filter(Boolean).join(", ") || "-";
        }
        return row?.makeLabel || "-";
      },
    },
    ...(!isOrderLevelDate
      ? [
          {
            title: "Need By Date",
            dataIndex: "needByDate",
            key: "needByDate",
            width: 140,
            render: (v: string) => fmtDate(v),
          },
        ]
      : []),
    {
      title: "HSN / SAC",
      dataIndex: "hsnCode",
      key: "hsnCode",
      width: 110,
    },
    {
      title: "Qty",
      key: "quantity",
      width: 130,
      align: "right" as const,
      render: (_: any, row: any) =>
        `${num(row?.quantity)}${row?.uomName ? ` ${row.uomName}` : ""}`,
    },
    {
      title: "Invoiced Qty",
      key: "quantityInvoiced",
      width: 140,
      align: "right" as const,
      render: (_: any, row: any) =>
        `${num(row?.quantityInvoiced)}${row?.uomName ? ` ${row.uomName}` : ""}`,
    },
    {
      title: "Delivered Qty",
      key: "quantityDelivered",
      width: 140,
      align: "right" as const,
      render: (_: any, row: any) =>
        `${num(row?.quantityDelivered)}${row?.uomName ? ` ${row.uomName}` : ""}`,
    },
    ...(isTransferred
      ? [
          {
            title: "Transfer Qty",
            key: "quantityTransferred",
            width: 140,
            align: "right" as const,
            render: (_: any, row: any) =>
              `${num(row?.quantityTransferred)}${row?.uomName ? ` ${row.uomName}` : ""}`,
          },
        ]
      : []),
    {
      title: "Unit Price",
      key: "unitPrice",
      width: 130,
      align: "right" as const,
      render: (_: any, row: any) => {
        const v = row?.unitPrice ?? row?.price;
        return v != null ? `${cur} ${v}` : "-";
      },
    },
    {
      title: "Basic Amount",
      key: "basic",
      width: 140,
      align: "right" as const,
      render: (_: any, row: any) => {
        const total = num(row?.quantity) * num(row?.unitPrice ?? row?.price);
        return total ? `${cur} ${total}` : "-";
      },
    },
    ...(!isIgst
      ? [
          {
            title: "CGST %",
            dataIndex: "cgstPercentage",
            key: "cgstPercentage",
            width: 90,
          },
          {
            title: "CGST Amt",
            dataIndex: "cgst",
            key: "cgst",
            width: 130,
            align: "right" as const,
            render: (v: any) => (v != null ? `${cur} ${v}` : "-"),
          },
          {
            title: "SGST %",
            dataIndex: "sgstPercentage",
            key: "sgstPercentage",
            width: 90,
          },
          {
            title: "SGST Amt",
            dataIndex: "sgst",
            key: "sgst",
            width: 130,
            align: "right" as const,
            render: (v: any) => (v != null ? `${cur} ${v}` : "-"),
          },
        ]
      : [
          {
            title: "IGST %",
            dataIndex: "igstPercentage",
            key: "igstPercentage",
            width: 90,
          },
          {
            title: "IGST Amt",
            dataIndex: "igst",
            key: "igst",
            width: 130,
            align: "right" as const,
            render: (v: any) => (v != null ? `${cur} ${v}` : "-"),
          },
        ]),
    {
      title: "Line Total",
      key: "grossTotal",
      width: 140,
      align: "right" as const,
      fixed: "right" as const,
      render: (_: any, row: any) => {
        const v =
          row?.grossTotal ?? row?.totalAmount ?? row?.totalValue ?? null;
        if (v != null) return `${cur} ${v}`;
        const computed =
          num(row?.quantity) * num(row?.unitPrice ?? row?.price) +
          num(row?.cgst) +
          num(row?.sgst) +
          num(row?.igst);
        return computed ? `${cur} ${computed}` : "-";
      },
    },
  ];

  const poId =
    order?.orderSequence || order?.orderNumber || orderNumber || "-";

  return (
    <ProjectPageShell
      headTitle="View Purchase Order"
      breadcrumb={[
        { label: projectName },
        { label: "Purchase Order" },
        { label: poId },
      ]}
      title={`PO - ${poId}`}
      eyebrow="Purchase Order"
      onBack={() => navigate(-1)}
      actions={
        headerStatus ? <GenericStatusPill status={headerStatus} /> : null
      }
      loading={loading}
    >
      <Collapse
                defaultActiveKey={["1", "2", "3", "4"]}
                items={[
                  {
                    key: "1",
                    label: "General Information",
                    children: (
                      <Descriptions
                        bordered
                        size="small"
                        column={{ xs: 1, sm: 2, md: 3 }}
                      >
                        <Descriptions.Item label="Order Date">
                          {fmtDate(order?.orderDate)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Site">
                          {order?.siteName || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Cost Center">
                          {order?.costCenterName || "-"}
                        </Descriptions.Item>

                        <Descriptions.Item label="Contractor">
                          {order?.contractorName || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Description">
                          {order?.description || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Ledger">
                          {order?.ledgerName || "-"}
                        </Descriptions.Item>

                        <Descriptions.Item label="Department">
                          {order?.departmentName || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Budget">
                          {order?.budgetName || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Payment Terms">
                          {order?.paymentTerms ? (
                            <Tag color="green">{order.paymentTerms}</Tag>
                          ) : (
                            "-"
                          )}
                        </Descriptions.Item>

                        {order?.advancePercentage ? (
                          <Descriptions.Item label="Advance %">
                            {order.advancePercentage}%
                          </Descriptions.Item>
                        ) : null}
                        {order?.creditDays ? (
                          <Descriptions.Item label="Credit Days">
                            <Tag color="blue">{order.creditDays}</Tag>
                          </Descriptions.Item>
                        ) : null}
                        {order?.contractReferenceId ? (
                          <Descriptions.Item label="Contract #">
                            {order.contractReferenceId}
                          </Descriptions.Item>
                        ) : null}
                        {order?.quotationId ? (
                          <Descriptions.Item label="Quotation #">
                            {order.quotationId}
                          </Descriptions.Item>
                        ) : null}

                        {order?.transferFromOrderSequenceId ? (
                          <Descriptions.Item label="Transfer From PO">
                            {order.transferFromOrderSequenceId}
                          </Descriptions.Item>
                        ) : null}
                        {order?.transferDate ? (
                          <Descriptions.Item label="Transfer Date">
                            {fmtDate(order.transferDate)}
                          </Descriptions.Item>
                        ) : null}
                        {order?.transferReason ? (
                          <Descriptions.Item label="Transfer Reason">
                            {order.transferReason}
                          </Descriptions.Item>
                        ) : null}
                        {order?.transferFromSupplierName ? (
                          <Descriptions.Item label="Transfer From Supplier">
                            {order.transferFromSupplierName}
                          </Descriptions.Item>
                        ) : null}
                      </Descriptions>
                    ),
                  },
                  {
                    key: "2",
                    label: "Delivery Information",
                    children: (
                      <Descriptions
                        bordered
                        size="small"
                        column={{ xs: 1, sm: 2, md: 4 }}
                      >
                        <Descriptions.Item label="Inward Required">
                          {order?.inwardRequired === "Y" ||
                          order?.inwardRequired === true
                            ? "Yes"
                            : order?.inwardRequired === "N" ||
                              order?.inwardRequired === false
                            ? "No"
                            : "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Line Level Delivery">
                          {(order?.needByDateType || "")
                            .toLowerCase()
                            .includes("line")
                            ? "Yes"
                            : "No"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Delivery Type">
                          {order?.deliveryAddressType || "-"}
                        </Descriptions.Item>
                        {isOrderLevelDate ? (
                          <Descriptions.Item label="Need By Date">
                            {fmtDate(order?.needByDate)}
                          </Descriptions.Item>
                        ) : (
                          <Descriptions.Item label="Need By Date">
                            Line Level
                          </Descriptions.Item>
                        )}

                        <Descriptions.Item label="Billing Address" span={2}>
                          <AddressPopover value={order?.billingAddressName} />
                        </Descriptions.Item>
                        {!isLineLevelAddress ? (
                          <Descriptions.Item label="Delivery Address" span={2}>
                            <AddressPopover value={order?.deliveryAddressName} />
                          </Descriptions.Item>
                        ) : null}

                        <Descriptions.Item label="Supplier" span={2}>
                          <div>
                            <div>
                              <strong>{order?.supplierName || "-"}</strong>
                            </div>
                            {order?.supplierGstin ? (
                              <div style={{ fontSize: 12, color: "#666" }}>
                                GSTIN: {order.supplierGstin}
                              </div>
                            ) : null}
                            {order?.supplierPan ? (
                              <div style={{ fontSize: 12, color: "#666" }}>
                                PAN: {order.supplierPan}
                              </div>
                            ) : null}
                            {order?.supplierCin ? (
                              <div style={{ fontSize: 12, color: "#666" }}>
                                CIN: {order.supplierCin}
                              </div>
                            ) : null}
                          </div>
                        </Descriptions.Item>
                      </Descriptions>
                    ),
                  },
                  {
                    key: "3",
                    label: `Material Information (${lines.length})`,
                    children: (
                      <>
                        <Table
                          className="customTable pms-hub-table"
                          columns={lineColumns}
                          dataSource={lines}
                          rowKey={(r: any, i?: number) =>
                            String(r?.lineNo ?? r?.id ?? i ?? Math.random())
                          }
                          pagination={false}
                          size="small"
                          scroll={{ x: 1800 }}
                          expandable={{
                            expandedRowRender: (record: any) => (
                              <div style={{ padding: "8px 12px" }}>
                                {record?.description ? (
                                  <div style={{ marginBottom: 6 }}>
                                    <strong>Description:</strong>{" "}
                                    {record.description}
                                  </div>
                                ) : null}
                                {isLineLevelAddress &&
                                record?.deliveryAddressName ? (
                                  <div>
                                    <strong>Delivery Address:</strong>{" "}
                                    <AddressPopover
                                      value={record.deliveryAddressName}
                                    />
                                  </div>
                                ) : null}
                              </div>
                            ),
                            rowExpandable: (record: any) =>
                              !!record?.description ||
                              (isLineLevelAddress &&
                                !!record?.deliveryAddressName),
                          }}
                          locale={{ emptyText: <Empty description="No items" /> }}
                        />

                        <div
                          style={{
                            marginTop: 16,
                            display: "flex",
                            justifyContent: "flex-end",
                          }}
                        >
                          <Descriptions
                            bordered
                            size="small"
                            column={1}
                            style={{ minWidth: 320 }}
                          >
                            <Descriptions.Item label="Taxable Amount">
                              {`${cur} ${totals.taxable.toFixed(2)}`}
                            </Descriptions.Item>
                            {!isIgst ? (
                              <>
                                <Descriptions.Item label="CGST">
                                  {`${cur} ${totals.cgst.toFixed(2)}`}
                                </Descriptions.Item>
                                <Descriptions.Item label="SGST">
                                  {`${cur} ${totals.sgst.toFixed(2)}`}
                                </Descriptions.Item>
                              </>
                            ) : (
                              <Descriptions.Item label="IGST">
                                {`${cur} ${totals.igst.toFixed(2)}`}
                              </Descriptions.Item>
                            )}
                            <Descriptions.Item label="Total">
                              <strong>
                                {`${cur} ${totals.gross.toFixed(2)}`}
                              </strong>
                            </Descriptions.Item>
                          </Descriptions>
                        </div>
                      </>
                    ),
                  },
                  {
                    key: "4",
                    label: "Other Information",
                    children: (
                      <>
                        {attachments.length > 0 ? (
                          <>
                            <h6 style={{ fontWeight: 600, margin: "0 0 8px" }}>
                              Attachments ({attachments.length})
                            </h6>
                            <ul style={{ paddingLeft: 18 }}>
                              {attachments.map((doc: any, idx: number) => (
                                <li key={doc?.docId ?? idx}>
                                  {doc?.filePath ? (
                                    <a
                                      href={doc.filePath}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      {doc?.fileName ||
                                        doc?.moduleFileType ||
                                        "View"}
                                    </a>
                                  ) : (
                                    <span>{doc?.fileName || "-"}</span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </>
                        ) : (
                          <Empty description="No additional information" />
                        )}

                        {order?.notes ? (
                          <div style={{ marginTop: 12 }}>
                            <strong>Notes:</strong> {order.notes}
                          </div>
                        ) : null}
                      </>
                    ),
                  },
                ]}
              />
    </ProjectPageShell>
  );
};

export default ViewPurchaseOrder;
